import { CostChannel } from '@sg/graphql-schema';

import {
  type MenuContentOrderChannel,
  useDeliveryMenuContentData,
  useMenuContentData,
} from '../../../../components';
import {
  useProductDetailsBaseProductQuery,
  useProductDetailsProductForRestaurantQuery,
} from '../../graphql/ProductDetailsProduct.generated';

// ─────────────────────────────────────────────────────────────────────────────

export const useProductDetailsRemoteData = (params: Params) => {
  const { baseProductSlug, restaurantId = '', deliveryAddressId } = params;

  const costChannel = CostChannel.DefaultCostChannel;

  // ─── Product Data ────────────────────────────────────────────────────

  const [baseProductQuery] = useProductDetailsBaseProductQuery({
    variables: { slug: baseProductSlug },
    requestPolicy: 'cache-and-network',
  });

  const [productForRestaurantQuery] =
    useProductDetailsProductForRestaurantQuery({
      requestPolicy: 'cache-and-network',
      variables: { baseProductSlug, costChannel, restaurantId },
      pause: !restaurantId,
    });

  // ─────────────────────────────────────────────────────────────────────

  const baseProduct = baseProductQuery.data?.baseProduct;
  const productForRestaurant = productForRestaurantQuery.data?.productForRestaurant; // prettier-ignore
  const restaurantSlug = productForRestaurant?.restaurant.slug;

  // ─── Restaurant Data ─────────────────────────────────────────────────

  const restaurantInfo = useMenuContentData({
    restaurantSlug,
    requestPolicy: 'cache-first',
  });
  const deliveryRestaurantInfo = useDeliveryMenuContentData({
    addressId: deliveryAddressId,
    requestPolicy: 'cache-first',
  });

  // ─── Derived Data ────────────────────────────────────────────────────

  const restaurantData = restaurantInfo.restaurantData;
  const deliveryDetailsData = deliveryRestaurantInfo.deliveryDetailsData;

  const restaurantName = restaurantInfo.restaurantData?.name ?? '';
  const isOutpostRestaurant = restaurantInfo?.restaurantData?.isOutpost === true; // prettier-ignore
  const deliveryLocationName = deliveryDetailsData?.addressName ?? deliveryDetailsData?.shortAddress ?? ''; // prettier-ignore

  const orderChannel = getOrderChannel(isOutpostRestaurant, deliveryAddressId);
  const locationName = deliveryAddressId ? deliveryLocationName : restaurantName; // prettier-ignore

  // ─── Flags ───────────────────────────────────────────────────────────

  const isFetching =
    restaurantInfo.isFetching ||
    deliveryRestaurantInfo.isFetching ||
    baseProductQuery.fetching ||
    productForRestaurantQuery.fetching;

  // ─────────────────────────────────────────────────────────────────────

  return {
    isFetching,
    baseProduct,
    productForRestaurant,
    orderChannel,
    locationName,
    restaurantData,
    deliveryDetailsData,
  };
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

function getOrderChannel(
  isOutpost: boolean,
  deliveryAddressId: string | undefined,
): MenuContentOrderChannel {
  if (isOutpost) {
    return 'outpost';
  }

  if (deliveryAddressId) {
    return 'delivery';
  }

  return 'pickup';
}

// ─── Types ───────────────────────────────────────────────────────────────────

type Params = {
  baseProductSlug: string;
  restaurantId: string | undefined;
  deliveryAddressId: string | undefined;
};
